import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import { Alert, Checkbox, Col, Collapse, Descriptions, Drawer, Dropdown, Form, MenuProps, message, Pagination, Row, Space, Table, Tag, Tooltip, Typography } from "antd";
// import { ExportDataSheetModal } from "../../common/ExportDataSheetModal";
import { StyledButton } from "src/styled_components/StyledButton";
import { useState, useMemo, ReactChild, ReactFragment, ReactPortal, useEffect, useCallback } from "react";
import { WorkOrdersData } from "../BayesianOptimization/GetData/GetData";
import { CommonCollapse } from "../BayesianOptimization/Constraints/Constraints";
// import type { ColumnsType } from "antd/es/table";
import './PredictionHistoryDetails.scss'
import StyledBadge from "src/styled_components/StyledBadge";
import useTranslate from "src/utils/useTranslate";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import FilterAndSorting from "./FilterandSort";
import { useQuery } from "src/utils/useQuery";

import { predictionStatus, predictionStatusColorText, predictionStatusIcon } from "src/utils/decorator";
import { getPredictionHistoryDetailFilterCleanup, getPredictionHistoryDetailFilterRequest, getPredictionHistoryDetailsDataCleanup, getPredictionHistoryDetailsDataRequest, getWorkOrderBayesianDataRequest } from "src/store/actions/extrapolation";
import { useValue } from "src/utils/useValue";
import { AsyncStates } from "src/constants";
import { WorkOrderModal } from "../../ForwardModel/WorkOrderModal";




const { Text } = Typography;

const columnStyle: React.CSSProperties = {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    borderRight: '1px solid #e8e8e8',
};

const HeaderRowObjectives = () => {
    const [t] = useTranslate();
    return (<Row style={{ borderBottom: '1px solid #e8e8e8', background: '#fafafa' }}>
        <Col span={6} style={columnStyle}><Text strong>{t("extrapolation.objective")}</Text></Col>
        <Col span={5} style={columnStyle}><Text strong>{t("exptrapolation.predHistory.defaultExpRange")}</Text></Col>
        <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.optimisationType")}</Text></Col>
        <Col span={3} style={columnStyle}><Text strong>{t("extrapolation.objective.minValue")}</Text></Col>
        <Col span={3} style={columnStyle}><Text strong>{t("extrapolation.objective.maxValue")}</Text></Col>
        <Col span={3} style={columnStyle}><Text strong>{t("extrapolation.objective.priority")}</Text></Col>
    </Row>)
};



const TableDataObjectives = ({ row }: { row: any }) => {
    const { convertValue } = useValue();
    return (
        <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
            <Col span={6} style={{ paddingLeft: 30 }}>
                {row?.name}
            </Col>
            <Col span={5} style={{ paddingLeft: 20 }}>
                {row?.startRange!==null && row?.startRange!==undefined && row?.endRange!==null && row?.endRange!==undefined ? `${convertValue(row?.startRange) ?? ""}-${convertValue(row?.endRange) ?? ""}` : ""}
            </Col>
            <Col span={4} style={{ paddingLeft: 20 }}>
                {row?.optimizationType || ""}
            </Col>
            <Col span={3} style={{ paddingLeft: 20 }}>
                {row?.minValue === "maximize" || row?.minValue === "minimize" || row?.minValue === "maximise" || row?.minValue === "minimise" ? "" : convertValue(row?.minValue) ?? ""}
            </Col>
            <Col span={3} style={{ paddingLeft: 20 }}>
                {row?.maxValue === "maximize" || row?.maxValue === "minimize" || row?.maxValue === "maximise" || row?.maxValue === "minimise" ? "" : convertValue(row?.maxValue) ?? ""}
            </Col>
            <Col span={3} style={{ paddingLeft: 20 }}>
                {row?.priority ?? ""}
            </Col>
        </Row>
    )
}

const HeaderRowContraints = () => {
    const [t] = useTranslate();
    return (<Row style={{ borderBottom: '1px solid #e8e8e8', background: '#fafafa' }}>
        <Col span={6} style={columnStyle}><Text strong>{t("extrapolation.predHistory.type")}</Text></Col>
        <Col span={5} style={columnStyle}><Text strong>{t("extrapolation.predHistory.inputs")}</Text></Col>
        <Col span={5} style={columnStyle}><Text strong>{t("exptrapolation.predHistory.defaultExpRange")}</Text></Col>
        <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.minValue")}</Text></Col>
        <Col span={4} style={columnStyle}><Text strong>{t("extrapolation.objective.maxValue")}</Text></Col>

    </Row>)
};

const TableDataConstraints = ({ row }: { row: any }) => {
    const { convertValue } = useValue();
    return (
        <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
            <Col span={6} style={{ paddingLeft: 30 }}>
                {row?.type}
            </Col>
            <Col span={5} style={{ paddingLeft: 20 }}>
                {row?.name.length > 30 ? <Tooltip title={row?.name?.length > 30 ? row?.name : undefined}>
                    <span style={{
                        display: 'inline-block',
                        maxWidth: 200, // adjust as needed
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'bottom'
                    }}>
                        {row?.name}
                    </span>
                </Tooltip> : row?.name}
            </Col>
            <Col span={5} style={{ paddingLeft: 20 }}>
                {row?.selectedOption?.length > 0 ? "Categorical" : row?.minValue != null && row?.maxValue != null && `${convertValue(row?.startRange) ?? ""}-${convertValue(row?.endRange) ?? ""}`}

            </Col>

            {row?.selectedOption?.length > 0 ? <Col span={8} style={{ paddingLeft: 20 }}>
                {row?.selectedOption?.map((item: any, index: number) => {
                    return (item) + `${index < row?.selectedOption?.length - 1 ? ", " : ""}`
                })}
            </Col> : <>
                <Col span={4} style={{ paddingLeft: 20 }}>
                    {convertValue(row?.minValue) ?? ""}
                </Col>
                <Col span={4} style={{ paddingLeft: 20 }}>
                    {convertValue(row?.maxValue) ?? ""}
                </Col>
            </>}
        </Row>
    )
}


type DataTypeKey = 'ingredients' | 'processing' | 'properties' | 'formulations';
interface PredictionHistoryDetailsProps {
    trialInputsData: Record<DataTypeKey, string[]>
    setTrialInputsData: React.Dispatch<React.SetStateAction<Record<DataTypeKey, string[]>>>
    userIgnoredData: Record<DataTypeKey, string[]>
    setUserIgnoredData: React.Dispatch<React.SetStateAction<Record<DataTypeKey, string[]>>>;
    predHistExtrapolationTitle: string;
}

const PredictionHistoryDetails: React.FC<PredictionHistoryDetailsProps> = (props) => {
    const { trialInputsData, setTrialInputsData, userIgnoredData, setUserIgnoredData, predHistExtrapolationTitle } = props;

    const [form] = Form.useForm()
    const query = useQuery()
    const dispatch = useDispatch();
    const [t] = useTranslate();
    const predId = query?.get('extrapolationId')
    const { convertValue } = useValue();

    const { predictionHistoryDetailsStatus, getWorkOrderBayesianDataStatus, workOrderBayesianData, predictionHistoryDetails, predictionHistoryDetailFilterStatus, predictionHistoryDetailFilter } = useSelector(
        (state: StoreState) => state.extrapolation,
    )
    const { current, selectAllProjects, } = useSelector(
        (state: StoreState) => state.projects,
    )
    const { properties, ingredients: ingredientsList, processing: processingList, characterizations } = useSelector((state: StoreState) => state.displayNames?.data);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
    const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 10 })
    // const [filterTablePagination, setFilterTablePagination] = useState({ current: 1, pageSize: 10 })
    const [isFilterSortingVisible, setIsFilterSortingVisible] =
        useState<boolean>(false)
    const [operatorStates, setOperatorStates] = useState<any[]>([])
    const [filters, setFilters] = useState<any>({})
    const [objectiveData, setObjectiveData] = useState<any>([]);
    const [constraintData, setConstraintData] = useState<any>([]);
    const [tableData, setTableData] = useState<any[]>([])
    const [filterData, setFilterData] = useState<any[]>([]);
    const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
    const [workOrderVisible, setWorkOrderVisible] = useState<boolean>(false); 
    const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const  [selectedExperiments, setSelectedExperiments] = useState<any[]>([]);
    const [historyTrialsData,setHistoryTrialsData] = useState<{data: any[], count: number}>({data: [],count: 0});

    const areAllCurrentPageItemsSelected = historyTrialsData?.data?.every(item => selectedItems.has(item?.exp));

    useEffect(() => {
        if(predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS) {
            setHistoryTrialsData({data: predictionHistoryDetailFilter?.data || [], count: predictionHistoryDetailFilter?.total_count || 0});
        }
        else{
            setHistoryTrialsData({data:predictionHistoryDetails?.results || [], count: predictionHistoryDetails?.total_result_count || 0});
        }
    },[predictionHistoryDetailFilter?.data, predictionHistoryDetailFilter?.total_count, predictionHistoryDetailFilterStatus, predictionHistoryDetails?.results, predictionHistoryDetails?.total_result_count])

    const dataList = useMemo(() => predictionHistoryDetailFilterStatus === AsyncStates.LOADING || predictionHistoryDetailsStatus === AsyncStates.LOADING
        ? []
        : historyTrialsData?.data, [predictionHistoryDetailFilterStatus, predictionHistoryDetailsStatus, historyTrialsData?.data]);


    const columns = useMemo(() => [
        {
                title: <Checkbox
                checked={areAllCurrentPageItemsSelected}
                onChange={(e:any) => {
                    const checked = e.target.checked;
                    // console.log(checked, 'checked')
                    if(checked){
                           
                            const item = new Set(selectedItems);
                            const newExp : any[] = [];
                            historyTrialsData?.data?.forEach((data:any) => {
                                if(!item.has(data?.exp)){
                                  item.add(data?.exp);
                                  newExp.push(data);
                                }
                            })
                            setSelectedItems(item);
                            setSelectedExperiments([...selectedExperiments,...newExp]);
                    }
                    else{
                        const item = new Set(selectedItems);
                        const toDelete = new Set();
                    
                        historyTrialsData?.data?.forEach((data: any) => {
                            item.delete(data?.exp);         // Remove from selectedItems
                            toDelete.add(data?.exp);        // Mark for removal from experiments
                        });
                    
                        setSelectedItems(item);
                    
                        // Filter out experiments with exp in toDelete
                        const updatedExperiments = selectedExperiments.filter(
                            (exp: any) => !toDelete.has(exp?.exp)
                        );

                        setSelectedExperiments(updatedExperiments); 
                    }
                }}
            >
                {`Suggested Experiments`}
            </Checkbox>,
            dataIndex: "label",
            key: "label",
            fixed: 'left' as const,
            width: 250,
            render: (
                text: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined,
                record: { isSection?: boolean; section?: string }
            ) => ({
                children: <span style={{ fontWeight: record.isSection ? 600 : 400 }}>{text}</span>,
                props: {
                    colSpan: record.isSection ? 11 : 1,
                    style: {
                        backgroundColor: record.isSection
                            ? "#fff"
                            : record.section === "Ingredients"
                                ? "#fff"
                                : record.section === "Processing"
                                    ? "#fff6e5"
                                    : record.section === "Properties"
                                        ? "#f6f9ff"
                                        : undefined,
                        fontWeight: record.isSection ? 600 : 400,
                    },
                },
            }),
        },
        {
            title: "Unit",
            dataIndex: "unit",
            key: "unit",
            width: 100,
            render: (text: any, record: { isSection?: boolean; section?: string }) => {
                if (record.isSection) {
                    return { children: null, props: { colSpan: 0 } };
                }

                let backgroundColor;
                if (record.section === "Ingredients") backgroundColor = "#fff";
                else if (record.section === "Processing") backgroundColor = "#fff6e5";
                else if (record.section === "Properties") backgroundColor = "#f6f9ff";

                return {
                    children: text,
                    props: {
                        style: { backgroundColor },
                    },
                };
            },
        },
        ...dataList?.map((_data: any, idx: number) => ({
                title: <Checkbox
                checked ={selectedItems.has(_data?.exp)}                
                onChange={(e:any) => {
                    const checked = e.target.checked;
                    if(checked){
                        const item = new Set(selectedItems);
                        item.add(_data?.exp);
                        setSelectedItems(item);
                        setSelectedExperiments([...selectedExperiments,_data]);
                    }
                    else{
                        const item = new Set(selectedItems);
                        item.delete(_data?.exp);
                        setSelectedItems(item);
                        setSelectedExperiments(selectedExperiments.filter((exp: any) => exp?.exp!== _data?.exp));
                    }

                }} >
                {_data?.trial_name?? ""}
            </Checkbox>,
            // title: _data?.trial_name ?? "",
            dataIndex: _data?.trial_name ?? "",
            key: _data?.trial_name ?? "",
            align: "center" as const,
            render: (text: any, record: { isSection?: boolean; section?: string }) => {
                if (record.isSection) {
                    return { children: null, props: { colSpan: 0 } };
                }

                let backgroundColor;
                // if (record.section === "Ingredients") backgroundColor = "#f6f9ff";
                if (record.section === "Processing") backgroundColor = "#fff6e5";
                else if (record.section === "Properties") backgroundColor = "#f6f9ff";

                return {
                    children: text,
                    props: {
                        style: {
                            backgroundColor,
                        },
                    },
                };
            },
        }))
    ], [areAllCurrentPageItemsSelected, dataList, historyTrialsData?.data, selectedExperiments, selectedItems]);

    useEffect(() => {
        if (!predictionHistoryDetails || !predictionHistoryDetails?.work_order_id) return;

        let payload: any = {
            page_num: pagination.current,
            page_size: pagination.pageSize,
            input_types: ["ingredients", "processing"],
            output_types: ["properties"],
            applications: [],
            materials: [],
            work_order_ids: [predictionHistoryDetails?.work_order_id],
            total_work_order_ids: [predictionHistoryDetails?.work_order_id],
            formulation_ids: [],
            project_ids: [],
            stage_identifier: [],
            ignore_formulation_ids: predictionHistoryDetails?.ignore_trials || [],
            is_multi_stage: false
        }

        dispatch(getWorkOrderBayesianDataRequest({ payload }))

    }, [current, dispatch, pagination, predictionHistoryDetails, selectAllProjects])

    //console.log(tableData, 'tableData')

    useEffect(() => {
        if (predictionHistoryDetailFilterStatus === AsyncStates.LOADING || predictionHistoryDetailsStatus === AsyncStates.LOADING) return;
        let initialIngredientsData: any[] = [{
            key: "section-1",
            label: `Ingredients (${Object.keys(predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS ? predictionHistoryDetailFilter?.data?.[0]?.data?.ingredients || {} : predictionHistoryDetailsStatus === AsyncStates.SUCCESS ? predictionHistoryDetails?.results?.[0]?.data?.ingredients || {} : {}).length || 0})`,
            isSection: true,
        }]
        let initialProcessingData: any[] = [{
            key: "section-2",
            label: `Processing (${Object.keys(predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS ? predictionHistoryDetailFilter?.data?.[0]?.data?.processing || {} : predictionHistoryDetailsStatus === AsyncStates.SUCCESS ? predictionHistoryDetails?.results?.[0]?.data?.processing || {} : {}).length || 0})`,
            isSection: true,
        }]
        // let initialPropertiesData: any[] = [{
        //     key: "section-3",
        //     label: `Properties (${Object.keys(predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS ? predictionHistoryDetailFilter?.[0]?.data?.predicted_properties || {} : predictionHistoryDetailsStatus === AsyncStates.SUCCESS ? predictionHistoryDetails?.results?.[0]?.data?.predicted_properties || {} : {}).length || 0})`,
        //     isSection: true
        // }]
        type FilterChild = { label: string; value: string };
        let initialFIlters: { label: string; value: string; children: FilterChild[] }[] = [
            { label: "Ingredients", value: "Ingredients", children: [] },
            { label: "Processing", value: "Processing", children: [] },
            { label: "Properties", value: "Properties", children: [] }
        ];
        let ingredientArr: { [x: string]: any; }[] = [];
        let processingArr: { [x: string]: any; }[] = [];
        let propertiesArr: { [x: string]: any; }[] = [];
        if (historyTrialsData?.data?.length === 0) {
            setTableData([])
            return;
        }

        historyTrialsData?.data?.forEach((item: {
            trial_name: any; data: { ingredients: any; processing: any; predicted_properties: any; };
        }, idx: number) => {
            if (!item?.data) return;
            // console.log(item,"item","came");
            // console.log(item?.trial_name,"item?.trial_name")
            let { ingredients = {}, processing = {}, predicted_properties = {} } = item?.data;

            if (idx === 0) {
                Object.entries(ingredients || {}).forEach(([key, value]: any) => {
                    // let meta = ingredientsList?.[key];
                    if (key && key in ingredientsList) {
                        let findFilter = initialFIlters?.find((item: any) => item?.label === "Ingredients");
                        if (findFilter) {
                            findFilter?.children?.push({ label: ingredientsList?.[key]?.name, value: key })
                        }
                        let obj = {
                            key: value?.category,
                            label: ingredientsList?.[key]?.name,
                            section: "Ingredients",
                            unit: (value as any)?.unit,
                            [item?.trial_name]: convertValue(ingredients[key]?.value),
                        }
                        ingredientArr.push(obj)
                    }
                })
                Object.entries(processing || {}).forEach(([key, value]: any) => {
                    // let meta = processingList?.[key];
                    if (key && key in processingList) {
                        let findFilter = initialFIlters?.find((item: any) => item?.label === "Processing");
                        if (findFilter) {
                            findFilter?.children?.push({ label: processingList?.[key]?.name, value: key })
                        }

                        let obj = {
                            key: value?.category,
                            label: processingList?.[key]?.name,
                            section: "Processing",
                            unit: (value as any)?.unit,
                            [item?.trial_name]: convertValue(processing[key]?.value),

                        }
                        processingArr.push(obj)
                    }
                })
                Object.entries(predicted_properties || {}).forEach(([key, value]: any) => {
                    // let meta = properties?.[key];
                    if (key && key in properties) {
                        let findFilter = initialFIlters?.find((item: any) => item?.label === "Properties");
                        if (findFilter) {
                            findFilter?.children?.push({ label: properties?.[key]?.name, value: key })
                        }
                        let obj = {
                            key: value?.category,
                            label: properties?.[key]?.name,
                            section: "Properties",
                            unit: (value as any)?.unit,
                            [item?.trial_name]: convertValue(predicted_properties[key]?.value),

                        }
                        propertiesArr.push(obj)
                    }
                })
            }
            else {
                Object.entries(ingredients || {}).forEach(([key, value]) => {
                    const obj = ingredientArr.find(obj => obj.label === ingredientsList?.[key]?.name);
                    if (obj) {
                        obj[item?.trial_name] = convertValue(ingredients[key]?.value);
                    }
                });
                Object.entries(processing || {}).forEach(([key, value]) => {
                    const obj = processingArr.find(obj => obj.label === processingList?.[key]?.name);
                    if (obj) {
                        obj[item?.trial_name] = convertValue(processing[key]?.value);
                    }
                });
                Object.entries(predicted_properties || {}).forEach(([key, value]) => {
                    const obj = propertiesArr.find(obj => obj.label === properties?.[key]?.name);
                    if (obj) {
                        obj[item?.trial_name] = convertValue(predicted_properties[key]?.value);
                    }
                });
            }

        })
        //console.log(ingredientArr,"ingredientArr",processingArr,"processingArr",propertiesArr,"propertiesArr");
        initialIngredientsData = initialIngredientsData.concat(ingredientArr)
        initialProcessingData = initialProcessingData.concat(processingArr)
        // initialPropertiesData = initialPropertiesData.concat(propertiesArr)
        setFilterData(initialFIlters.filter((item: any) => item?.children?.length > 0))
        // setTableData(initialIngredientsData.concat(initialProcessingData).concat(initialPropertiesData))
        setTableData(initialIngredientsData.concat(initialProcessingData))

    }, [predictionHistoryDetailFilterStatus, predictionHistoryDetailFilter, ingredientsList, predictionHistoryDetails, predictionHistoryDetails?.results, processingList, properties, convertValue, predictionHistoryDetailsStatus, historyTrialsData?.data])


    useEffect(() => {
        let objectiveArr: { name: any; identifier: string; maxValue: unknown; startRange: unknown; endRange: unknown; minValue: unknown; priority: any; optimizationType: unknown; }[] = []
        if (Object.keys(predictionHistoryDetails?.objectives || {})?.length > 0) {
            Object.entries(predictionHistoryDetails?.objectives || {}).forEach(([key, value]) => {
                const meta = properties?.[key];
                if (meta) {
                    objectiveArr?.push({
                        name: meta?.name,
                        identifier: key,
                        maxValue: (typeof value === 'object' && value !== null && 'max' in value) ? (value as any).max : value,
                        minValue: (typeof value === 'object' && value !== null && 'min' in value) ? (value as any).min : value,
                        startRange: convertValue(predictionHistoryDetails?.general_constraints?.Properties?.[key]?.min) ?? "",
                        endRange: convertValue(predictionHistoryDetails?.general_constraints?.Properties?.[key]?.max) ?? "",
                        priority: predictionHistoryDetails?.priority_list?.[key],
                        optimizationType: typeof value === "object" ? "range" : value
                    })
                }
            })



        }
        setObjectiveData(objectiveArr)

    }, [convertValue, predictionHistoryDetails?.general_constraints, predictionHistoryDetails?.objectives, predictionHistoryDetails?.priority_list, properties])

    useEffect(() => {
        if (Object.keys(predictionHistoryDetails?.feature_bounds || {})?.length > 0) {
            let initialConstaintData: { key: any; type: string; name: any; minValue?: any; maxValue?: any; selectedOption?: any; }[] = []
            Object.entries(predictionHistoryDetails?.feature_bounds || {}).forEach(([key, value]) => {
                let name = ingredientsList?.[key] ? "Ingredients" : processingList?.[key] ? "Processing" : characterizations?.[key] ? "Characterizations" : "";
                const meta = ingredientsList?.[key] || processingList?.[key] || characterizations?.[key];

                if (Array.isArray(value)) {
                    let obj =
                    {
                        key: meta?.identifier,
                        type: name,
                        name: meta?.name || key,
                        selectedOption: value
                    }
                    initialConstaintData.push(obj)
                }
                else {
                    let startRange = name === "Ingredients" ? convertValue(predictionHistoryDetails?.general_constraints?.Ingredients?.[key]?.min) ?? "" : name === "Processing" ? convertValue(predictionHistoryDetails?.general_constraints?.Processings?.[key]?.min) ?? "" : name === "Characterizations" ? convertValue(predictionHistoryDetails?.general_constraints?.Characterizations?.[key]?.min) ?? "" : "";
                    let endRange = name === "Ingredients" ? convertValue(predictionHistoryDetails?.general_constraints?.Ingredients?.[key]?.max) ?? "" : name === "Processing" ? convertValue(predictionHistoryDetails?.general_constraints?.Processings?.[key]?.max) ?? "" : name === "Characterizations" ? convertValue(predictionHistoryDetails?.general_constraints?.Characterizations?.[key]?.max) ?? "" : "";
                    let obj =
                    {
                        key: meta?.identifier,
                        type: name,
                        name: meta?.name || key,
                        startRange: startRange,
                        endRange: endRange,
                        minValue: typeof value === 'object' && value !== null && 'min' in value ? (value as any).min : undefined,
                        maxValue: typeof value === 'object' && value !== null && 'max' in value ? (value as any).max : undefined
                    }
                    initialConstaintData.push(obj)


                }

            })
            setConstraintData(initialConstaintData)
        }

    }, [characterizations, convertValue, ingredientsList, predictionHistoryDetails?.feature_bounds, predictionHistoryDetails?.general_constraints?.Characterizations, predictionHistoryDetails?.general_constraints?.Ingredients, predictionHistoryDetails?.general_constraints?.Processings, processingList])


    const generateWO = useCallback(
    	(type: string, selectAll: boolean = false) => {

    			if (selectAll) {
    				setIsSelectAll(true)
                    setSelectedExperiments([])
                    setSelectedItems(new Set())
    				setWorkOrderVisible(true)
    			} else if (selectedExperiments.length > 0) {
    				setIsSelectAll(false)
    				setWorkOrderVisible(true)
    			} else {
    				message.warning(t("aiEngine.wo.error"))
    			}

    	},
    	[selectedExperiments, setIsSelectAll, setWorkOrderVisible, t],
    )

    const generateWoMenuItems: MenuProps = useMemo(() => {
        return {
          items: [
             {
                key: 'generate-wo-selected-trials',
                label: 'Generate Work Order (Selected Trials)',
                disabled:predictionHistoryDetailsStatus === AsyncStates.LOADING || predictionHistoryDetailFilterStatus === AsyncStates.LOADING
            },
            {
                key: 'generate-wo-all-trials',
                label: 'Generate Work Order (All Trials)',
               disbaled:predictionHistoryDetailsStatus === AsyncStates.LOADING || predictionHistoryDetailFilterStatus === AsyncStates.LOADING
            },
          ],
          onClick: ({ key }: any) => {
    			generateWO("suggested", key === "generate-wo-all-trials")

          },
        }
      }, [generateWO, predictionHistoryDetailFilterStatus, predictionHistoryDetailsStatus])

    useEffect(() => {
        return () => {
            dispatch(getPredictionHistoryDetailsDataCleanup())
            dispatch(getPredictionHistoryDetailFilterCleanup())
            setUserIgnoredData({
                ingredients: [],
                processing: [],
                properties: [],
                formulations: []
            })
            setTrialInputsData({
                ingredients: [],
                processing: [],
                properties: [],
                formulations: []
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const applyFilters = (value: any) => {
        let { expFilters, sorting } = value;
        let filtersPayload = []
        let sortingPayload = []
        if (!!expFilters?.length) {
            filtersPayload = expFilters
                .filter((item: any) => !!item)
                .reduce(
                    (array: any, res: any) => [
                        ...array,
                        res.property?.[1] === "total_cost"
                            ? {
                                category: res.property?.[1],
                                field: "",
                                operand: res.operand,
                                min_value: String(convertValue(res.minVal)),
                                value_type: "number",
                                ...(res?.maxVal && { max_value: String(convertValue(res.maxVal)) }),
                            }
                            : {
                                category: res.property?.[0],
                                field: res.property?.[1],
                                operand: res.operand,
                                min_value: String(convertValue(res.minVal)),
                                value_type: "number",
                                ...(res?.maxVal && { max_value: String(convertValue(res.maxVal)) }),
                            },
                    ],
                    [],
                )
        }

        if (!!sorting?.length) {
            sortingPayload = sorting
                .filter((item: any) => !!item)
                .reduce(
                    (array: any, res: any) => [
                        ...array,
                        res.property?.[1] === "total_cost"
                            ? {
                                category: res.property?.[1],
                                field: "",
                                sort_order: res.sort_order,
                            }
                            : {
                                category: res.property?.[0],
                                field: res.property?.[1],
                                sort_order: res.sort_order,
                            },
                    ],
                    [],
                )
        }

        let payload = {
            prediction_id: predId,
            page_num: 1,
            page_size: 10,
            filters: filtersPayload?.map((data: any) => ({ ...data, category: data?.category?.toLowerCase() })),
            sorts: sortingPayload?.map((data: any) => ({ ...data, category: data?.category?.toLowerCase() }))
        }
        setTablePagination({ current: 1, pageSize: 10 })
        setIsFilterApplied(true)
        setFilters(payload)

    }

    useEffect(() => {
        if (isFilterApplied && Object.keys(filters || {}).length > 0) {
            let payload = {
                prediction_id: predId,
                page_num: tablePagination.current,
                page_size: tablePagination.pageSize,
                filters: filters.filters,
                sorts: filters.sorts
            }
            dispatch(getPredictionHistoryDetailFilterRequest({ payload }))
        }
    }, [dispatch, filters, isFilterApplied, predId, tablePagination])


    useEffect(() => {
        let payload = {
            prediction_id: predId,
            page_num: tablePagination.current,
            page_size: tablePagination.pageSize,
        }
        // console.log(predId, "predId");
        if (predId && !isFilterApplied) {
            dispatch(getPredictionHistoryDetailsDataRequest({ payload }))
        }

    }, [dispatch, predId, tablePagination, isFilterApplied])



    function pageChange(pageNum: number, pageSize: number) {
        setTablePagination({ current: pageNum, pageSize })
        // setSelectAll(false)

    }

    useEffect(() => {
        if (predictionHistoryDetailsStatus === AsyncStates.SUCCESS && predictionHistoryDetailsStatus === AsyncStates.SUCCESS && Object.keys(predictionHistoryDetails || {}).length > 0 && workOrderBayesianData && workOrderBayesianData?.dataframe?.length > 0) {
            let ingredients = new Set(Object.keys(workOrderBayesianData?.dataframe?.[0]?.ingredients || {}))
            let processing = new Set(Object.keys(workOrderBayesianData?.dataframe[0]?.processing?.[0]?.processing || {}))
            let properties = new Set(Object.keys(workOrderBayesianData?.dataframe[0]?.properties?.[0]?.properties || {}))
            let ignoredInputs = predictionHistoryDetails?.ignore_inputs ?? []
            let formulations = predictionHistoryDetails?.ignore_trials ?? []
            ingredients?.forEach(item => {
                if (!ignoredInputs.includes(item)) {
                    ingredients.delete(item);
                }
            });

            processing?.forEach(item => {
                if (!ignoredInputs.includes(item)) {
                    processing.delete(item);
                }
            });

            properties?.forEach(item => {
                if (!ignoredInputs.includes(item)) {
                    properties.delete(item);
                }
            });
            setUserIgnoredData({
                ingredients: Array.from(ingredients),
                processing: Array.from(processing),
                properties: Array.from(properties),
                formulations: formulations
            })
        }
    }, [predictionHistoryDetailsStatus, predictionHistoryDetails, workOrderBayesianData, setTrialInputsData, setUserIgnoredData])

    const makePayloadForWorkOrder = () => {
        let data: any[] = []
        let arr: { experiment_id: any; algorithm: any; created: any; custom_feature_ranges: any; formulation_id: any; ingredients: any; input_constraints: any; is_multistage: any; objectives: any; predicted_properties: any; prediction_id: any; priority_list: any; work_order_id: any; project_id: any; status: any; title: any; trial_name: string; updated: any; user_id: any; version: any; parameter_constraints: {}; display_names: {}; results_range: {}; all_stages: never[]; processing: any; }[] = []
        if (predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS && Array.isArray(predictionHistoryDetailFilter?.data)) {
            data = predictionHistoryDetailFilter?.data
        }
        else if (predictionHistoryDetails?.results && Array.isArray(predictionHistoryDetails?.results)) {
            data = predictionHistoryDetails?.results
        }
        else {
            return []
        }
        data?.forEach((item: any, index: number) => {
            let obj = {
                experiment_id: item?.exp,
                algorithm: item?.algorithm,
                created: item?.created,
                custom_feature_ranges: item?.data?.custom_feature_ranges,
                formulation_id: item?.data?.formulation_id,
                ingredients: item?.data?.ingredients,
                input_constraints: item?.data?.input_constraints,
                is_multistage: predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS ? predictionHistoryDetailFilter?.data?.[index]?.is_multistage ?? false : predictionHistoryDetails?.is_multistage ?? false,
                objectives: item?.data?.objectives,
                predicted_properties: item?.data?.predicted_properties,
                prediction_id: item?.prediction_id,
                priority_list: predictionHistoryDetailFilterStatus === AsyncStates.SUCCESS ? predictionHistoryDetailFilter?.data?.[index]?.priority_list ?? {} : predictionHistoryDetails?.priority_list ?? {},
                work_order_id: item?.work_order_id?.[0],
                project_id: item?.project_id,
                status: item?.status,
                title: item?.title,
                trial_name: item?.trial_name,
                updated: item?.updated,
                user_id: item?.user_id,
                version: item?.version,
                parameter_constraints: {},
                display_names: {},
                results_range: {},
                all_stages: [],
                processing: item?.data?.processing,
            }
            arr.push(obj)
        })
        return arr

    }
    //console.log(tablePagination, "tablePagination", filterTablePagination, "filterTablePagination")
    //  console.log(makePayloadForWorkOrder(selectedExperiments),"makePayloadForWorkOrder",predictionHistoryDetails,"predictionHistoryDetails",predictionHistoryDetailFilter,"predictionHistoryDetailFilter",tableData,"tableData")
    return (<Space
        size="large"
        direction="vertical"
        style={{ width: "100%", overflow: "hidden" }}
    >
        {/* Top action */}
        <Row justify="end" gutter={8}>
            <Col>
                <Dropdown menu={generateWoMenuItems} placement="bottomRight">
                    <StyledButton type="primary">
                        {t("extrapolation.predHistory.generateWorkOrder")}
                        <DownOutlined />
                    </StyledButton>
                </Dropdown>
            </Col>

            {/* <Col>
                <Tooltip
                    placement="top"
                    title={'exportDatasheet'}
                >
                    <StyledButton
                        type="default"
                        icon={<DownloadOutlined />}
                    // disabled={predictionDataExportStatus === AsyncStates.LOADING}
                    // onClick={() => {
                    //     setExportDataSheetModalData({
                    //         isModalVisible: true,
                    //         include_range: true
                    //     })
                    // }}
                    />
                </Tooltip>
            </Col> */}

        </Row>
         <WorkOrderModal
            workOrderVisible={workOrderVisible}
            setWorkOrderVisible={setWorkOrderVisible}
            checkedExperiments={selectedExperiments?.map((val: any) => val.exp)}
            experiments={makePayloadForWorkOrder() || []}
            type={"bayesian"}
            isSelectAll={isSelectAll}
        />
     <Drawer
            open={selectedExperiments?.length > 0}
            placement="bottom"
            style={{
                background: "#262626",
            }}
            height={125}
            mask={false}
            maskClosable={false}
            closeIcon={false}
            title={
                <>
                    <Space direction="vertical" size={"large"} style={{
                        width: '100%',
                    }}>
                        <Typography.Title
                            level={5}
                            style={{ margin: 0, color: "#fff" }}
                        >{`${selectedExperiments?.length} ${t(
                            "common.trials",
                        )} ${t("formulations.selected")}`}</Typography.Title>
                        <div
                            style={{
                                display: "flex",
                                overflowX: "auto",
                            }}
                        >
                            {selectedExperiments?.map(
                                (item: any, idx: number) => {
                                    return (
                                        <Tag
                                            closable
                                            onClose={(e) => {
                                                const _data = new Set(selectedItems);
                                                _data.delete(item?.exp);
                                                setSelectedItems(_data);
                                                setSelectedExperiments(selectedExperiments.filter((exp: any) => exp?.exp!== item?.exp));
                                            }}
                                            style={{
                                                background: "#262626",
                                                borderColor: "#fff",
                                                color: "#fff",
                                                lineHeight: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: 'max-content',
                                                width: 'max-content',
                                            }}
                                            closeIcon={
                                                <CloseOutlined
                                                    style={{ color: "#fff" }}
                                                />
                                            }
                                            key={`${item?.exp}-${item?.index}`}
                                        >
                                            <Text
                                                style={{ color: "#fff" }}
                                            >{item?.trial_name}</Text>
                                        </Tag>
                                    )
                                },
                            )}
                        </div>
                    </Space>
                    <div style={{ position: "absolute", top: "-12%", left: "50%" }}>
                        <StyledButton
                            onClick={() => {
                               setSelectedItems(new Set())
                               setSelectedExperiments([])
                            }}
                            style={{
                                background: "#262626",
                                color: "#fff",
                                borderColor: "#fff",
                            }}
                        >
                            <Space>
                                <CloseOutlined />
                                {t("common.clear")}
                            </Space>
                        </StyledButton>
                    </div>
                </>
            }
            styles={{
                body: {
                    display: "none",
                },
                header: {
                    padding: 24,
                    height: '100%',
                },
            }}
        />

        {/* {
                    exportDataSheetModalData.isModalVisible &&
                    <ExportDataSheetModal exportDataSheetModalData={exportDataSheetModalData} setExportDataSheetModalData={setExportDataSheetModalData} loadingState={predictionDataExportStatus} onSubmit={exportDataSheet} from="history-prediction" />
                } */}

        {/* Model detials */}
        <Row
            style={{
                padding: "16px",
                borderRadius: "8px",
                backgroundColor: "#FAFAFA",
            }}
        >
            <Descriptions
                layout="vertical"
                colon={false}
                size="small"
                className="suggested-exp-description"
                column={5}
                bordered={true}
            >
                <Descriptions.Item
                    label={"Objective/ Title"}
                >
                    <Space style={{ width: "100%" }}>

                        <Text
                            style={{
                                maxWidth: "300px",
                            }}
                            ellipsis={{ tooltip: predHistExtrapolationTitle || predictionHistoryDetails?.title }}
                            strong
                        >
                            {predHistExtrapolationTitle || predictionHistoryDetails?.title }
                        </Text>


                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label={"Data Source"}>
                    <Text
                        style={{ maxWidth: "300px" }}
                        ellipsis={{
                            tooltip: "test 2"
                        }}
                        strong
                    >
                        {"Work Order"}
                    </Text>
                </Descriptions.Item>
                <Descriptions.Item label={"status"}>
                    {predictionHistoryDetails?.status === "in_progress" ?
                        <Tag
                            color={predictionStatusColorText["in_progress"]}
                            icon={predictionStatusIcon["in_progress"]}
                            style={{
                                textTransform: "capitalize",
                                border: "1px solid",
                                fontWeight: "bold",
                            }}
                        >
                            {predictionStatus["in_progress"]}
                        </Tag> : predictionHistoryDetails?.status === "no_results_found" ?
                            <Tag
                                color={predictionStatusColorText["no_results_found"]}
                                icon={predictionStatusIcon["no_results_found"]}
                                style={{
                                    textTransform: "capitalize",
                                    border: "1px solid",
                                    fontWeight: "bold",
                                }}
                            >
                                {predictionStatus["no_results_found"]}
                            </Tag> : predictionHistoryDetails?.status === "failed" ? <Tag
                                color={predictionStatusColorText["failed"]}
                                icon={predictionStatusIcon["failed"]}
                                style={{
                                    textTransform: "capitalize",
                                    border: "1px solid",
                                    fontWeight: "bold",
                                }}
                            >
                                {predictionStatus["failed"]}
                            </Tag> : <Tag
                                color={predictionStatusColorText["Completed"]}
                                icon={predictionStatusIcon["Completed"]}
                                style={{
                                    textTransform: "capitalize",
                                    border: "1px solid",
                                    fontWeight: "bold",
                                }}
                            >
                                {predictionStatus["Completed"]}
                            </Tag>}
                </Descriptions.Item>
            </Descriptions>
        </Row>
        <Space direction="vertical" style={{ width: "100%" }}>
            <Collapse
                defaultActiveKey={['1']}
                items={[
                    {
                        key: '1',
                        label: 'View Data',
                        children: (
                            <WorkOrdersData
                                from={"history-prediction-details"}
                                trialInputsData={trialInputsData}
                                setTrialInputsData={setTrialInputsData}
                                userIgnoredData={userIgnoredData}
                                setUserIgnoredData={setUserIgnoredData}
                                pagination={pagination}
                                setPagination={setPagination}
                                data={workOrderBayesianData}
                                workOrder={
                                    predictionHistoryDetails?.work_order_id
                                        ? [predictionHistoryDetails.work_order_id]
                                        : []
                                }
                                workOrderName={predictionHistoryDetails?.work_order_name}
                                getWorkOrderBayesianDataStatus={getWorkOrderBayesianDataStatus}
                            />
                        ),
                    },
                ]}
            />
        </Space>
        {objectiveData?.length > 0 && (
            <CommonCollapse
                labelValue="Objective"
                headerRows={<HeaderRowObjectives />}
                tableData={(row, idx) => <TableDataObjectives row={row} />}
                list={objectiveData}
            />
        )}
        {constraintData?.length > 0 && (
            <CommonCollapse
                labelValue="Constraints"
                headerRows={<HeaderRowContraints />}
                tableData={(row, idx) => <TableDataConstraints row={row} />}
                list={constraintData}
            />
        )}

        <Pagination
            size="small"
            current={tablePagination.current}
            pageSize={tablePagination.pageSize}
            showSizeChanger={false}
            responsive={true}
            style={{ textAlign: "right" }}
            total={historyTrialsData?.count || 0}

            onChange={pageChange}
        />
        <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            loading={predictionHistoryDetailsStatus === AsyncStates.LOADING || predictionHistoryDetailFilterStatus === AsyncStates.LOADING}
            scroll={{ x: "max-content" }}
            size="small"
            bordered={false}
            rowClassName={(record) => {
                if (record.isSection) return "section-row";
                return "data-row";
            }}
            style={{ fontSize: 13, }}
            title={() => (
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Row justify="space-between">
                        <Typography.Title level={5}>
                            {`${historyTrialsData?.count ?? 0} ${t("common.trials")}`}
                        </Typography.Title>
                        <Row gutter={8}>
                            <Col>
                                <StyledBadge
                                    // dot={
                                    //   filters?.expFilters?.length > 0 ||
                                    //   filters?.sorts?.length > 0
                                    // }
                                    style={{ marginRight: 5 }}
                                >
                                    <StyledButton
                                        onClick={() =>
                                            setIsFilterSortingVisible((prevState) => !prevState)
                                        }
                                        className={
                                            isFilterSortingVisible ? "action-button-active" : ""
                                        }
                                        style={{ marginRight: 5 }}
                                    >
                                        {t("aiEngine.filters.title")}
                                    </StyledButton>
                                </StyledBadge>
                            </Col>
                        </Row>
                    </Row>

                    <Alert
                        description={
                            <FilterAndSorting
                                form={form}
                                applyFilters={applyFilters}
                                filterData={filterData}
                                operatorStates={operatorStates}
                                setOperatorStates={setOperatorStates}
                                filters={filters}
                                setFilters={setFilters}
                                setIsFilterApplied={setIsFilterApplied}
                                setTablePagination={setTablePagination}
                            />
                        }
                        type="info"
                        message={
                            <Row justify={"space-between"}>
                                <Typography.Title
                                    level={5}
                                >
                                    {t("aiEngine.filters.title")}
                                </Typography.Title>
                                <CloseOutlined
                                    onClick={() => setIsFilterSortingVisible(false)}
                                    style={{ outline: "none" }}
                                />
                            </Row>
                        }
                        style={{
                            border: "none",
                            padding: "16px",
                            backgroundColor: "#FAFAFA",
                            display: isFilterSortingVisible ? "flex" : "none",
                        }}
                    />
                </Space>
            )}
        />

        <div
            style={{
                paddingBottom:
                    selectedExperiments.length > 0 ? 125 : 10,
            }}
        ></div>

    </Space>


    )
}

export default PredictionHistoryDetails;
